import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { Betrag } from './Betrag'
import { Abrufausfall, useAbruf } from './Abruf'
import { api, betrag as formatiert, imHintergrund, periodenname } from './api'
import type { Aufloesungszeitpunkt } from './api'
import { Dialogfenster } from './Dialogfenster'
import { fachlicherSatz } from './Fehlersatz'
import { meldeErfolg } from './Erfolgsstelle'
import {
  anwendername,
  anzahlwort,
  belegerlaeuterung,
  belegtext,
  bewegungsartname,
  gesellschaftsname,
  kontobezeichnung,
  ohneVortragszeilen,
  t,
  zeitpunkt,
} from './sprache'
import type { Sprachschluessel } from './sprache'
import { ohneWert } from './Ohnewert'
import { Zeilenmenue } from './Zeilenmenue'
import type { Menuepunkt } from './Zeilenmenue'
import { Belegmaske } from './Belegmaske'
import { Journalsuche, Spaltenkopf } from './Journalsuche'
import { Nachfrage } from './Nachfrage'
import { Ordnerbaum, Ordnersymbol, belegeZiehen, jahresordnerVon } from './Ordnerbaum'
import { useDarf } from './Rechte'
import { ariaSortierung, naechste, sortiere } from './Sortierung'
import type { Sortierwahl } from './Sortierung'
import type { Buchungsvorbelegung } from './Belegmaske'
import { Spaltenmassleiste, useSpaltenmasse } from './Spaltenbreiten'
import type {
  Belegordner as Ordner,
  Erfassungszeile,
  Loeschvorschau,
  Ordnerbeleg,
  Protokolleintrag,
  Protokollzeile,
} from './typen'

/**
 * Das Journal als Belegordner (2.16, R375).
 *
 * **Ein freier Baum je Gesellschaft.** Das Journal wird Gesellschaft für
 * Gesellschaft geführt; die erste Ebene kommt deshalb aus den Stammdaten und
 * nicht aus der Ablage. Der *Eingang* ist unentfernbar und trägt, was ohne
 * Ordnerwahl entsteht — dieselbe Bauform wie die erste Kachel des
 * Kachelbretts (R337).
 *
 * **Verschieben fasst den Beleg nicht an.** Die Zuordnung Beleg → Ordner liegt
 * in einer eigenen Tabelle; Strg+X und Strg+V sind eine Umsortierung, die
 * keinen Wert berührt, keinen Prüfcode bewegt und in keinen Lauf eingeht. Eine
 * Ablagefrage darf die Schreibregel des Journals nicht aufweichen.
 *
 * **Einfügen fragt** (R376): Duplikat mit denselben Beträgen — oder Vorlage,
 * die alle Merkmale mitnimmt und keine Zahl.
 */

/** Solange der Baum nicht da ist, steht kein Ordner darin (R1210). */
const KEINE_ORDNER: Ordner[] = []

/** Was die Nachfrage je Zeitpunkt der Auflösung sagt (R394, R1832). */
const UMKEHRTEXTE: Record<'folgeperiode' | Aufloesungszeitpunkt, { titel: Sprachschluessel; frage: Sprachschluessel; hinweis: Sprachschluessel }> = {
  folgeperiode: { titel: 'beleg.menue.umkehren', frage: 'beleg.umkehren.frage', hinweis: 'beleg.umkehren.hinweis' },
  jahresende: {
    titel: 'beleg.menue.umkehren.jahresende',
    frage: 'beleg.umkehren.jahresende.frage',
    hinweis: 'beleg.umkehren.jahresende.hinweis',
  },
  'neues-jahr': {
    titel: 'beleg.menue.umkehren.neues-jahr',
    frage: 'beleg.umkehren.neues-jahr.frage',
    hinweis: 'beleg.umkehren.neues-jahr.hinweis',
  },
}

const BELEGART: Record<string, Sprachschluessel> = {
  lieferung: 'journal.art.lieferung',
  erfassung: 'journal.art.erfassung',
  vortrag: 'journal.art.vortrag',
  umrechnung: 'journal.art.umrechnung',
  konsolidierung: 'journal.art.konsolidierung',
  storno: 'journal.art.storno',
  zuordnung: 'journal.art.zuordnung',
  ueberleitung: 'journal.art.ueberleitung',
}

const EREIGNIS: Record<string, Sprachschluessel> = {
  angelegt: 'protokoll.ereignis.angelegt',
  geaendert: 'protokoll.ereignis.geaendert',
  umbenannt: 'protokoll.ereignis.umbenannt',
  verschoben: 'protokoll.ereignis.verschoben',
  umgekehrt: 'protokoll.ereignis.umgekehrt',
  storniert: 'protokoll.ereignis.storniert',
  geloescht: 'protokoll.ereignis.geloescht',
}

/** Was in der Zwischenablage liegt und was daraus werden soll (R376). */
type Ablage = { beleg: Ordnerbeleg; schneiden: boolean }

/**
 * Die Spalten der Belegliste — **jede sortierbar** (2026-09-12).
 *
 * Der Vorrat steht hier und nicht im Markup, weil Kopf und Sortierschlüssel
 * zusammengehören: Eine Spalte, die man anklicken kann und die dann nach etwas
 * anderem sortiert, als in ihr steht, ist schlimmer als eine, die sich nicht
 * anklicken lässt.
 */
const BELEGSPALTEN: { id: string; kopf: Sprachschluessel; zahl?: boolean }[] = [
  { id: 'nummer', kopf: 'vorgang.spalte.nummer' },
  { id: 'name', kopf: 'beleg.name' },
  { id: 'periode', kopf: 'journal.periode' },
  /*
    **Der Stand statt der Summe und der Zeilenzahl** (2026-09-12, auf Einwand
    des Auftraggebers).

    Beide Spalten standen hier und beantworteten nichts: *„Betrag ist mit einer
    Zahl auch nicht hilfreich, weil eine einzige Zahl keine Aussage bei einem
    37-Zeilen-Beleg hat"* — und eine Zeilenzahl sagt auf dieser Flughöhe erst
    recht nichts. Beides steht weiterhin da, wo es etwas sagt: im
    aufgeschlagenen Beleg, Zeile für Zeile.

    An ihrer Stelle steht die Frage, die man einer Belegliste wirklich stellt:
    **wann wurde daran zuletzt gearbeitet.**
  */
  { id: 'stand', kopf: 'journal.spalte.stand' },
  { id: 'anwender', kopf: 'protokoll.spalte.anwender' },
]

/** Was eine Spalte der Belegliste zum Vergleichen hergibt. */
function belegschluessel(b: Ordnerbeleg, spalte: string): string | number | null {
  switch (spalte) {
    case 'nummer':
      return b.nummer
    case 'name':
      // Sortiert wird nach dem gespeicherten Namen, nicht nach dem gelesenen
      // (7.2 Abschnitt 21, R839): Mit Monatsnamen stünde „… April 2026" vor
      // „… Januar 2026".
      return b.name
    case 'periode':
      return b.periode
    case 'stand':
      // Sortiert wird nach dem Zeitstempel, nicht nach seiner Beschriftung:
      // „12.9.2026" stünde als Text vor „2.10.2026".
      return b.geaendertAm ?? b.erfasstAm
    default:
      return b.geaendertVon ?? b.erfasstVon
  }
}

/* Die zwei Ordnersymbole stehen beim Ordnerbaum (R980). */
export { Ordnersymbol }

export function Belegordner({
  ledger,
  periode,
  filterperiode,
  kreis,
  einheiten,
  einheit,
  aufEinheiten,
  aufEinheit,
  ziel,
  aufZielErreicht,
  sicht,
}: {
  ledger: string
  /** Der Stichtag der Berichte — er wählt beim Öffnen das Jahr und belegt eine neue Buchung vor. */
  periode: number
  /**
   * Die Periode des Journals (R986) — `null` heißt: alle. Sie grenzt die
   * Belege und die Zahlen am Baum ein; der Baum selbst bleibt.
   */
  filterperiode: number | null
  kreis: string
  /** Die Gesellschaften — sie liegen oben, weil beide Sichten dieselbe zeigen sollen. */
  einheiten: { schluessel: string; code: string; namen: Record<string, string> }[]
  einheit: string
  aufEinheiten: (liste: { schluessel: string; code: string; namen: Record<string, string> }[]) => void
  aufEinheit: (schluessel: string) => void
  /** Der Sprung aus der Trefferliste: Ordner auf, Beleg aufgeschlagen (R209). */
  ziel?: { einheit: string; ordnerId: number | null; belegId: number } | null
  aufZielErreicht: () => void
  /**
   * Was im rechten Feld steht: die Belege des Ordners oder die Treffer der
   * Suche.
   *
   * **Das Brett bleibt in beiden Fällen stehen.** Bis zum 2026-09-12 löste die
   * Suche es auf: Der Baum verschwand, und jeder Ordner wurde zu einer Zelle
   * in der ersten Spalte — gleichwertig zu Konto und Betrag daneben. Damit war
   * er kein *Ort* mehr, sondern ein Merkmal.
   */
  sicht: 'belege' | 'suche'
}) {
  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse, rahmen: spaltenrahmen } =
    useSpaltenmasse<HTMLTableElement>('belegordner:belegordner')

  /** Die gezogene Breite einer Spalte — solange keine gezogen ist, gilt die Klasse. */
  const gezogen = (i: number) =>
    spaltenmasse?.[i] === undefined ? undefined : { width: `${spaltenmasse[i]}px` }

  /*
    **Die erste Ebene sind die Gesellschaften** (R375), und sie werden hier
    geholt statt aus dem Beschriftungskatalog gelesen. Der Katalog wird beim
    Start asynchron gefüllt und löst kein Neuzeichnen aus — ein Bauteil, das
    ihn beim ersten Zeichnen liest, bekommt eine leere Liste und behält sie.
  */
  useEffect(() => {
    if (einheiten.length > 0) {
      return
    }

    let gilt = true
    // Ein Nachschlagen im Hintergrund (7.2 Abschnitt 30, R1007).
    imHintergrund(() => api.stammdatenkatalog())
      .then((katalogsatz) => {
        if (!gilt) return
        const liste = [...katalogsatz.einheiten].sort((a, b) => a.code.localeCompare(b.code))
        aufEinheiten(liste)
        // Leer oder gemerkt und nicht mehr da: die erste der Liste (R1364).
        if (einheit === '' || !liste.some((e) => e.schluessel === einheit)) {
          aufEinheit(liste[0]?.schluessel ?? '')
        }
      })
      .catch(() => undefined)

    return () => {
      gilt = false
    }
  }, [einheiten.length, einheit, aufEinheiten, aufEinheit])
  const [gewaehlt, setGewaehlt] = useState<number | null>(null)
  const [ablage, setAblage] = useState<Ablage | null>(null)
  const [menue, setMenue] = useState<{ x: number; y: number; beleg: Ordnerbeleg } | null>(null)
  const [maske, setMaske] = useState<Buchungsvorbelegung | null>(null)
  const [loeschen, setLoeschen] = useState<Loeschvorschau | null>(null)
  const [protokoll, setProtokoll] = useState<{ nummer: string; eintraege: Protokolleintrag[] } | null>(null)
  const [einfuegen, setEinfuegen] = useState<Ablage | null>(null)
  /** Der Beleg, dessen Umkehrung nachgefragt wird (R394), und wann sie endet (R1832). */
  const [umkehrfrage, setUmkehrfrage] = useState<{ beleg: Ordnerbeleg; zeitpunkt?: Aufloesungszeitpunkt } | null>(null)
  const [meldung, setMeldung] = useState<string | null>(null)
  /**
   * Die Nummer des Belegs, der eben entstand (7.2 Abschnitt 35, R1136) — er
   * steht hinterlegt und ins Bild gerollt, bis in die Liste geklickt wird.
   * An der Nummer erkannt, weil das Buchen nur sie zurückgibt.
   */
  const [neu, setNeu] = useState<string | null>(null)
  const belegkoerper = useRef<HTMLTableSectionElement>(null)
  const [stand, setStand] = useState(0)
  const [offen, setOffen] = useState<Ordnerbeleg | null>(null)
  /*
    **Baum, Belegliste und Belegzeilen sind Hauptabrufe** (7.2 Abschnitt 39,
    R1211). Bis zum 2026-10-06 wurde ein Ausfall zur leeren Liste, und unter
    dem Ordner stand „In diesem Ordner liegt kein Beleg." Jetzt steht am Ort
    „Ließ sich nicht laden" (R1210).
  */
  const ordnerabruf = useAbruf(
    einheit ? () => api.belegordner(einheit, ledger, filterperiode) : null,
    [einheit, ledger, periode, filterperiode, stand],
  )
  const ordner = ordnerabruf.wert ?? KEINE_ORDNER
  // Die Liste eines anderen Ordners wird beim Wechsel sofort verworfen.
  const belegabruf = useAbruf(
    gewaehlt === null ? null : () => api.ordnerbelege(gewaehlt, ledger, filterperiode),
    [gewaehlt, ledger, filterperiode, stand],
    true,
  )
  const belege = belegabruf.wert
  // Der geöffnete Beleg zeigt seine Zeilen — dieselbe Auskunft wie im Journal,
  // nur an der Stelle, an der der Anwender gerade steht.
  const zeilenabruf = useAbruf<Erfassungszeile[]>(
    offen === null
      ? null
      : () =>
          api.belegzeilen(offen.id).then((z) =>
            z.map((x) => ({
              konto: x.konto,
              betrag: x.betrag,
              bewegungsart: x.bewegungsart,
              icPartner: x.icPartner,
              segment: x.segment,
              funktionsbereich: x.funktionsbereich,
              erlaeuterung: x.erlaeuterung,
              dim1: x.dimensionen?.d1 ?? null,
              dim2: x.dimensionen?.d2 ?? null,
              dim3: x.dimensionen?.d3 ?? null,
              dim4: x.dimensionen?.d4 ?? null,
              dim5: x.dimensionen?.d5 ?? null,
              dim6: x.dimensionen?.d6 ?? null,
              dim7: x.dimensionen?.d7 ?? null,
              dim8: x.dimensionen?.d8 ?? null,
            })),
          ),
    [offen],
  )
  const zeilen = zeilenabruf.wert
  // Was der Angemeldete nicht darf, steht nicht da — kein Knopf, kein
  // Menüpunkt, keine Taste, kein Ziehen (R1180).
  const darf = useDarf()
  const darfAblegen = darf('belegAblegen')
  const darfBuchen = darf('buchen')

  /**
   * Wonach die Belegliste sortiert ist — null heißt: in der Ordnung des
   * Ordners, und die trägt eine Aussage (Periode absteigend, dann
   * Buchungsreihenfolge).
   */
  const [wahl, setWahl] = useState<Sortierwahl>(null)

  /**
   * Wie viele Treffer der Suche in welchem Ordner liegen.
   *
   * **Der Baum sagt beim Suchen, wo etwas liegt** — das ist die Auskunft, die
   * man beim Suchen wirklich braucht, und der Grund, warum er stehen bleibt,
   * während die Trefferliste die Belegliste ersetzt.
   */
  const [trefferJeOrdner, setTrefferJeOrdner] = useState<Map<number | null, number>>(
    () => new Map(),
  )

  /**
   * Die eigene Reihenfolge des Anwenders (R392) — **neben** der automatischen
   * Sortierung, nicht statt ihrer.
   *
   * Sie liegt hier als Liste von Kennungen, solange sie geschrieben wird; die
   * Wahrheit steht in `belegablage.sortierung` und kommt beim nächsten Laden
   * von dort.
   */
  const [eigeneFolge, setEigeneFolge] = useState<number[] | null>(null)

  const liste = useRef<HTMLDivElement | null>(null)

  const neuLaden = useCallback(() => setStand((n) => n + 1), [])

  // Der neue Beleg rollt ins Bild, sobald die Liste ihn trägt (R1136).
  useEffect(() => {
    if (neu !== null) {
      belegkoerper.current?.querySelector('tr.neu')?.scrollIntoView?.({ block: 'nearest' })
    }
  }, [neu, belege])

  /*
    **Ein Gesellschaftswechsel schließt, was aufgeschlagen war.**

    Das Auswahlfeld steht seit dem 2026-09-12 oben in der Journalleiste und
    nicht mehr hier; sein Klickverhalten kann diesen Zustand also nicht mehr
    selbst zurücksetzen. Ohne das bliebe der Beleg der alten Gesellschaft unter
    dem Baum der neuen stehen — die falscheste Auskunft, die dieses Brett geben
    kann.
  */
  useEffect(() => {
    setOffen(null)
    setMenue(null)
  }, [einheit])

  // Kommt der Baum, bleibt die Wahl stehen, solange es den Ordner noch gibt.
  useEffect(() => {
    const o = ordnerabruf.wert
    if (o === null) return

    // Ohne Wahl: das Jahr des gewählten Monats, sonst des Stichtags, sonst das jüngste (R984).
    setGewaehlt((alt) =>
      alt !== null && o.some((x) => x.id === alt)
        ? alt
        : (jahresordnerVon(o, filterperiode ?? periode)?.id ??
            o.filter((x) => x.istEingang).at(-1)?.id ??
            null),
    )
  }, [ordnerabruf.wert])

  /*
    **Der Sprung aus der Trefferliste führt an die Stelle** (R209): Ordner
    auswählen, Beleg aufschlagen — nicht in eine gefilterte Liste, in der der
    Anwender ihn dann sucht.

    Der Beleg liegt oft in einer anderen Periode als der eingestellten; die
    Suche läuft über alle. Deshalb wird er hier **einzeln geholt** statt in der
    Ordnerliste gesucht — sonst führte der Sprung ins Leere, sobald der
    Periodenfilter dazwischensteht.
  */
  /*
    **Der Ordnerbaum steht in einem Verweis und nicht in den Abhängigkeiten.**

    Er gehörte dort einmal hin, und das war der Fehler: Beim Wechsel in diese
    Sicht lädt der Baum nach, seine Kennung wechselt, der Effekt läuft ein
    zweites Mal — und sein Aufräumen verwirft die Belegabfrage, die noch
    unterwegs war. Der Ordner ging auf, der Beleg blieb zu. Gebraucht wird der
    Baum hier ohnehin nur für einen Blick: welcher Ordner der Eingang ist.
  */
  const baumverweis = useRef<Ordner[]>(ordner)
  baumverweis.current = ordner

  /**
   * In einen Ordner springen und einen Beleg aufschlagen (R209).
   *
   * **Ein Weg für zwei Anlässe:** den Sprung aus der Trefferliste des Berichts
   * (über `ziel`) und den Klick in der Journalsuche. Zwei Wege wären zwei
   * Gelegenheiten, den Beleg zuzulassen.
   */
  const springeZu = useCallback(async (ordnerId: number | null, belegId: number) => {
    if (ordnerId !== null) {
      setGewaehlt(ordnerId)
    }

    const kopf = (await api.journal({ beleg: belegId })).at(0)
    if (!kopf) {
      return false
    }

    // Ohne eigenen Ordner steht er im Jahresordner seiner Periode (R984).
    if (ordnerId === null) {
      setGewaehlt(jahresordnerVon(baumverweis.current, kopf.periode)?.id ?? null)
    }

    setOffen({
      id: kopf.id,
      nummer: kopf.nummer,
      belegart: kopf.belegart,
      name: kopf.name,
      ledger: kopf.ledger,
      periode: kopf.periode,
      erlaeuterung: kopf.erlaeuterung,
      summe: kopf.summe,
      zeilen: kopf.zeilen,
      storniertDurch: kopf.storniertDurch,
      kehrtUm: kopf.umkehrtNummer,
      umgekehrtDurch: kopf.umgekehrtDurch,
      erfasstAm: kopf.erfasstAm,
      erfasstVon: kopf.erfasstVon,
      belegtyp: kopf.belegtyp ?? 'sonstiges',
      geaendertAm: kopf.geaendertAm ?? null,
      belegartSagtMehr: kopf.belegartSagtMehr ?? true,
    })

    return true
  }, [])

  useEffect(() => {
    if (!ziel) {
      return
    }

    let gilt = true

    void (async () => {
      if (ziel.ordnerId !== null) {
        setGewaehlt(ziel.ordnerId)
      }

      const kopf = (await api.journal({ beleg: ziel.belegId })).at(0)

      /*
        **Ein verworfener Lauf meldet nichts.**

        Stand hier einmal `if (!gilt || !kopf) { aufZielErreicht(); return }` —
        und genau das machte den Sprung unter `StrictMode` unmöglich: React
        ruft einen Effekt beim Einhängen zweimal auf, der erste Lauf wird
        aufgeräumt (`gilt = false`), meldete aber trotzdem „Ziel erreicht".
        Damit war das Ziel gelöscht, bevor der zweite Lauf seine Antwort hatte
        — der Ordner ging auf, der Beleg blieb zu. Wer abgeräumt wurde, sagt
        nichts mehr.
      */
      if (!gilt) {
        return
      }

      if (!kopf) {
        aufZielErreicht()
        return
      }

      // Ohne eigenen Ordner steht er im Jahresordner seiner Periode (R984).
      if (ziel.ordnerId === null) {
        setGewaehlt(jahresordnerVon(baumverweis.current, kopf.periode)?.id ?? null)
      }

      setOffen({
        id: kopf.id,
        nummer: kopf.nummer,
        belegart: kopf.belegart,
        name: kopf.name,
        ledger: kopf.ledger,
        periode: kopf.periode,
        erlaeuterung: kopf.erlaeuterung,
        summe: kopf.summe,
        zeilen: kopf.zeilen,
        storniertDurch: kopf.storniertDurch,
        kehrtUm: kopf.umkehrtNummer,
        umgekehrtDurch: kopf.umgekehrtDurch,
        erfasstAm: kopf.erfasstAm,
        erfasstVon: kopf.erfasstVon,
        belegtyp: kopf.belegtyp ?? 'sonstiges',
        geaendertAm: kopf.geaendertAm ?? null,
        belegartSagtMehr: kopf.belegartSagtMehr ?? true,
      })

      aufZielErreicht()
    })()

    return () => {
      gilt = false
    }
  }, [ziel, aufZielErreicht])

  /*
    **Die üblichen Microsoft-Kürzel** — Strg+C, Strg+X, Strg+V, Entf, F2 und
    Einfg. Sie greifen nur, solange der Anwender in der Liste steht und kein
    Fenster offen ist: Ein Strg+C im Textfeld einer Maske gehört dem Textfeld.
  */
  useEffect(() => {
    function beiTaste(e: KeyboardEvent) {
      if (maske || loeschen || protokoll || einfuegen) return

      const ziel = e.target as HTMLElement | null
      if (ziel && ['INPUT', 'TEXTAREA', 'SELECT'].includes(ziel.tagName)) return

      const beleg = offen

      // Kopieren führt nur zur Maske eines neuen Belegs, Ausschneiden nur zum Ablegen.
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c' && beleg && darfBuchen) {
        e.preventDefault()
        setAblage({ beleg, schneiden: false })
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'x' && beleg && darfAblegen) {
        e.preventDefault()
        setAblage({ beleg, schneiden: true })
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v' && ablage) {
        e.preventDefault()
        void fuegeEin(ablage)
      } else if (e.key === 'Delete' && beleg && darf('belegLoeschen')) {
        e.preventDefault()
        void frageLoeschen(beleg)
      } else if (e.key === 'Insert' && darfBuchen) {
        e.preventDefault()
        buchen()
      }
    }

    window.addEventListener('keydown', beiTaste)
    return () => window.removeEventListener('keydown', beiTaste)
  })

  /**
   * Einfügen — **nach Ausschneiden ohne Rückfrage, nach Kopieren mit** (R376).
   *
   * Beide Absichten fangen mit Strg+C an und unterscheiden sich erst beim
   * Einfügen. Wer sie vorne trennt, verlangt vom Anwender, beim Kopieren schon
   * zu wissen, was er später will.
   */
  async function fuegeEin(was: Ablage) {
    if (gewaehlt === null) return

    if (was.schneiden) {
      try {
        await api.belegAblegen(was.beleg.id, gewaehlt)
        setAblage(null)
      } catch (e) {
        // Etwa ein Ordner eines anderen Jahres (R985) — der Satz sagt es.
        setMeldung(fachlicherSatz(e))
      }
      neuLaden()
      return
    }

    setEinfuegen(was)
  }

  async function ausAblage(was: Ablage, alsVorlage: boolean) {
    setEinfuegen(null)

    const zeilen = await zeilenVon(was.beleg.id)

    setMaske({
      einheit,
      ledger,
      periode: buchungsperiode,
      kreis,
      ordnerId: gewaehlt,
      ...(alsVorlage
        ? { vorlage: zeilen }
        : {
            duplikat: {
              name: was.beleg.name ?? was.beleg.nummer,
              erlaeuterung: was.beleg.erlaeuterung,
              zeilen,
            },
          }),
    })
  }

  /** Die gespeicherten Zeilen eines Belegs als Erfassungszeilen — ein Weg für
   *  Vorlage, Duplikat und Berichtigung. */
  async function zeilenVon(belegId: number): Promise<Erfassungszeile[]> {
    const roh = await api.belegzeilen(belegId)
    return roh.map<Erfassungszeile>((x) => ({
      konto: x.konto,
      betrag: x.betrag,
      bewegungsart: x.bewegungsart,
      icPartner: x.icPartner,
      segment: x.segment,
      funktionsbereich: x.funktionsbereich,
      erlaeuterung: x.erlaeuterung,
      dim1: x.dimensionen?.d1 ?? null,
      dim2: x.dimensionen?.d2 ?? null,
      dim3: x.dimensionen?.d3 ?? null,
      dim4: x.dimensionen?.d4 ?? null,
      dim5: x.dimensionen?.d5 ?? null,
      dim6: x.dimensionen?.d6 ?? null,
      dim7: x.dimensionen?.d7 ?? null,
      dim8: x.dimensionen?.d8 ?? null,
    }))
  }

  /** Eine neue Buchung bekommt den gewählten Monat vorbelegt, bei „Alle" den Stichtag (R986). */
  const buchungsperiode = filterperiode ?? periode

  function buchen() {
    setMaske({ einheit, ledger, periode: buchungsperiode, kreis, ordnerId: gewaehlt })
  }

  /**
   * **Den Beleg berichtigen** (R388).
   *
   * Es ist dieselbe Maske, gefüllt mit dem, was drinsteht. Bewertungsebene und Periode
   * kommen aus dem **Beleg**, nicht aus der Leiste oben: Wer im Juli steht und
   * einen Junibeleg berichtigt, berichtigt einen Junibeleg — die Datierung ist
   * unveränderlich, und die Maske darf nichts anderes behaupten.
   */
  async function bearbeite(b: Ordnerbeleg) {
    try {
      setMaske({
        einheit,
        ledger: b.ledger,
        periode: b.periode,
        kreis,
        ordnerId: gewaehlt,
        bearbeiten: {
          belegId: b.id,
          nummer: b.nummer,
          name: b.name ?? '',
          erlaeuterung: b.erlaeuterung,
          // **Vorbelegt wie Name und Erläuterung** (R388). Bis zum 2026-10-05
          // stand sie leer, und der Knopf blieb grau. Die alte steht nach dem
          // Speichern im Änderungsprotokoll (R390).
          begruendung: b.begruendung ?? '',
          zeilen: await zeilenVon(b.id),
        },
      })
    } catch (e) {
      setMeldung(fachlicherSatz(e))
    }
  }

  // Ein Befund steht über dem Ordner, eine Störung im Störungsfenster
  // (7.2 Abschnitt 30, R1003/R1004) — vorher lief beides ins Leere.
  async function frageLoeschen(beleg: Ordnerbeleg) {
    try {
      setLoeschen(await api.loeschvorschau(beleg.id))
    } catch (e) {
      setMeldung(fachlicherSatz(e))
    }
  }

  async function zeigeProtokoll(beleg: Ordnerbeleg) {
    try {
      setProtokoll({ nummer: beleg.nummer, eintraege: await api.belegprotokoll(beleg.id) })
    } catch (e) {
      setMeldung(fachlicherSatz(e))
    }
  }

  async function kehreUm(beleg: Ordnerbeleg, zeitpunkt?: Aufloesungszeitpunkt) {
    try {
      const { nummer } = await api.umkehren(beleg.id, zeitpunkt)
      // Ohne die Nummer (7.2 Abschnitt 34, R1131) — der Umkehrbeleg steht
      // markiert in der Liste (R1136).
      meldeErfolg(t('erfolg.beleg.aufgeloest'))
      setMeldung(null)
      setNeu(nummer)
      neuLaden()
    } catch (e) {
      setMeldung(fachlicherSatz(e))
    }
  }

  const ordnerVon = (id: number | null) => ordner.find((o) => o.id === id) ?? null
  const aktuell = ordnerVon(gewaehlt)

  /**
   * Wie viele Belege in einem Ordner **und allem darunter** liegen.
   *
   * **Der Baum zeigt diese Zahl und nicht die unmittelbare.** Bis zum
   * 2026-09-12 stand neben „Abgrenzungen 2025" eine **0**, während in seinen
   * zwei Unterordnern fünf Belege lagen — und ein Ordner, der voll ist und
   * leer aussieht, ist die Sorte Auskunft, wegen der jemand zweimal sucht.
   * Die unmittelbare Zahl steht weiter im Kopf über der Liste; auseinander
   * gehen die beiden nur dort, wo es Unterordner gibt, und dann sagt der Kopf
   * es dazu.
   */
  function gesamtzahl(id: number): number {
    return ordner
      .filter((o) => o.eltern === id)
      .reduce((summe, kind) => summe + gesamtzahl(kind.id), ordnerVon(id)?.belege ?? 0)
  }

  /** Wie viele davon in Unterordnern liegen — die Differenz, die der Kopf nennt. */
  const inUnterordnern = aktuell ? gesamtzahl(aktuell.id) - aktuell.belege : 0

  /*
    **Die eigene Reihenfolge ist die Ordnung der Liste** (R392) — und damit
    genau das, wohin der dritte Klick auf einen Spaltenkopf zurückführt
    (R375b). Die beiden greifen ineinander: Die automatische Sortierung ist
    eine Sicht, die manuelle ist der Zustand darunter.

    **Von Hand geordnet wird nur in einem benannten Ordner.** Im Eingang liegt,
    was kein Ordner hält — dort gibt es keine Ablagezeile, in der eine Stellung
    stehen könnte. Der Weg, etwas in eine Reihenfolge zu bringen, ist dort:
    ablegen.
  */
  // Ohne Recht keine Griffe und keine Griffspalte (R1180).
  const vonHandOrdenbar =
    darf('belegreihenfolge') && sicht === 'belege' && wahl === null && aktuell?.istEingang === false

  const gezeigteBelege = (() => {
    const liste = belege ?? []
    if (wahl !== null || eigeneFolge === null) {
      return sortiere(liste, wahl, belegschluessel)
    }

    // Solange die neue Reihenfolge geschrieben wird, zeigt die Liste sie
    // bereits — sonst springt die Zeile beim Verschieben sichtbar zurück.
    const nach = new Map(liste.map((b) => [b.id, b]))
    return eigeneFolge.map((id) => nach.get(id)).filter((b): b is Ordnerbeleg => b !== undefined)
  })()

  /** Einen Beleg um eine Stelle verschieben — und die Reihenfolge festhalten. */
  async function verschiebe(stelle: number, schritt: number) {
    const ziel = stelle + schritt
    if (gewaehlt === null || ziel < 0 || ziel >= gezeigteBelege.length) {
      return
    }

    const neu = [...gezeigteBelege]
    const [bewegt] = neu.splice(stelle, 1)
    neu.splice(ziel, 0, bewegt!)

    const folge = neu.map((b) => b.id)
    setEigeneFolge(folge)
    await api.belegreihenfolge(gewaehlt, folge)
  }

  /** In welchem Ordner ein Treffer der Suche steht — ohne eigenen im Jahresordner (R984). */
  const ortVon = (x: { ordnerId: number | null; periode: number }) =>
    x.ordnerId ?? jahresordnerVon(ordner, x.periode)?.id ?? null

  return (
    <>
      {meldung && <p className="probe stimmt">{meldung}</p>}

      {/*
        **Die Zwischenablage sagt, was sie hält** — und zwar dauerhaft, solange
        sie etwas hält.

        Bis zum 2026-09-12 stand nach einem Strg+X der Text „Ausschneiden
        (Strg+X)" als **grüne Erfolgsmeldung** da: die Beschriftung eines
        Menüpunkts, benutzt als Statussatz, in der Farbe für „hat geklappt",
        und sie blieb stehen, nachdem längst eingefügt war. Drei Fehler in
        einer Zeile. Was der Anwender wissen muss, ist nicht *dass* er
        geschnitten hat — das sieht er an der blassen Zeile —, sondern **was
        in der Ablage liegt und wie es wieder herauskommt.**
      */}
      {ablage && (
        <p className="zwischenablage">
          <span className="was">
            {t(ablage.schneiden ? 'ablage.ausgeschnitten' : 'ablage.kopiert', {
              nummer: ablage.beleg.nummer,
              // Auch im Satz liest sich der gespeicherte Text mit Namen und Monat (R838, R839).
              name: belegtext(ablage.beleg.name ?? ablage.beleg.erlaeuterung),
            })}
          </span>
          <button type="button" onClick={() => void fuegeEin(ablage)} disabled={gewaehlt === null}>
            {t('ablage.einfuegen.hier', { ordner: aktuell ? aktuell.name : '—' })}
          </button>
          <button type="button" onClick={() => setAblage(null)}>
            {t('ablage.verwerfen')}
          </button>
        </p>
      )}

      {/*
        **Ein Brett aus zwei Feldern mit einer Trennkante** — nicht zwei
        Bauteile nebeneinander. Vorher stand der Baum als grauer Kasten neben
        einer Tabelle, und nichts sagte, dass die Belege IN ihm liegen. Drei
        Dinge sagen es jetzt: der gemeinsame Rahmen, der **Kopf über der
        Liste**, der den offenen Ordner beim Namen nennt, und der
        **aufgeklappte Ordner** im Baum. Dieselbe Auskunft dreifach — Form,
        Ort und Wort.
      */}
      <div className="ordnerbrett" ref={liste}>
        <nav className="ordnerbaum" aria-label={t('ordner.titel')}>
          <h3>{t('ordner.titel')}</h3>
          {/*
            **Ein Baum für beide Seiten** (R980): Rechtsklick, Tasten und
            Ziehen kommen aus `Ordnerbaum`. Die drei Knöpfe, die bis zum
            2026-10-03 im Fuß standen, sind im Menü aufgegangen.

            **Beim Suchen zählt der Baum Treffer statt Belege** — genau die
            Frage, die man beim Suchen an einen Ordner hat. Sonst zählt die
            Zahl den Teilbaum mit, und sie steht auch bei null da.
          */}
          {ordnerabruf.ausgefallen ? (
            <Abrufausfall abruf={ordnerabruf} />
          ) : (
            <ul>
              <Ordnerbaum
                einheit={einheit}
                ordner={ordner}
                gewaehlt={gewaehlt}
                aufWahl={(id) => {
                  setGewaehlt(id)
                  setOffen(null)
                }}
                zahl={(o) =>
                  sicht === 'suche' ? (trefferJeOrdner.get(o.id) ?? 0) : gesamtzahl(o.id)
                }
                aufGeaendert={neuLaden}
                belegablage={
                  ablage?.schneiden && darfAblegen
                    ? {
                        perioden: [ablage.beleg.periode],
                        einfuegen: async (ziel) => {
                          await api.belegAblegen(ablage.beleg.id, ziel)
                          setAblage(null)
                        },
                      }
                    : null
                }
                aufBelegeGezogen={async (ids, ziel) => {
                  for (const id of ids) {
                    await api.belegAblegen(id, ziel)
                  }
                }}
              />
            </ul>
          )}
        </nav>

        <div className="ordnerinhalt">
          {/*
            **Der Kopf ist die Brücke.** Er wiederholt, was links angeklickt
            wurde — Gesellschaft, dann Ordner, mit demselben aufgeklappten
            Symbol — und sagt daneben, wie viele Belege darin liegen. Wer von
            der Tabelle kommt, weiß damit ohne Hinsehen nach links, was er vor
            sich hat.
          */}
          {aktuell && (
            <header className="ordnerkopf">
              <span className="pfad">
                <span className="gesellschaft">
                  {gesellschaftsname(einheit)}
                </span>
                <span className="trenner" aria-hidden="true">
                  ›
                </span>
                <Ordnersymbol offen />
                <span className="name">{aktuell.name}</span>

                {/*
                  **Der Geltungsbereich gehört an den Kopf, nicht in die
                  Anleitung** (R144).

                  Aufgefallen am 2026-09-12 an dreißig Belegen: Oben stand
                  „Periode: Juni 2025", und darunter lagen Mai-Belege. Seit
                  dem 2026-10-03 filtert kein Ordner mehr nach der Periode:
                  Der Jahresordner zeigt sein ganzes Geschäftsjahr (R984), ein
                  eigener Ordner, was ein Mensch hineingelegt hat. Der Kopf
                  sagt es, damit die Periode oben nicht etwas anderes
                  behauptet.
                */}
                <span className="bereich">
                  {filterperiode ? periodenname(filterperiode) : t('ordner.allePerioden')}
                </span>
              </span>
              <span className="menge">
                {t(aktuell.belege === 1 ? 'ordner.beleg' : 'ordner.belege', {
                  anzahl: anzahlwort(aktuell.belege),
                })}
                {inUnterordnern > 0 && (
                  <span className="darunter">
                    {' '}
                    {t('ordner.inUnterordnern', { anzahl: anzahlwort(inUnterordnern) })}
                  </span>
                )}
              </span>

              {/*
                **Buchen steht am Ordner, in den gebucht wird** (R330).

                Es hatte bis zum 2026-09-12 eine eigene Leiste darüber — eine
                ganze Bildschirmzeile für einen Knopf und ein Auswahlfeld. Der
                Knopf gehört ohnehin hierher: Er schreibt in **diesen** Ordner,
                und dieser Kopf nennt ihn beim Namen. Die Gesellschaft steht
                jetzt oben bei den anderen beiden Filtern.
              */}
              {darfBuchen && (
                <button type="button" className="buchenknopf" onClick={buchen}>
                  {t('beleg.buchen')}
                </button>
              )}
            </header>
          )}

          {/*
            **Die Suche steht im Feld des Ordners, nicht an seiner Stelle.**
            Links der Baum mit seinen Trefferzahlen, rechts die Treffer — der
            Ordner bleibt der Ort, an dem etwas liegt.
          */}
          {sicht === 'suche' && (
            <Journalsuche
              einheit={einheit}
              nurOrdner={gewaehlt}
              /*
                **Der Jahresordner ist kein gespeicherter Ordner, sondern das
                Fehlen eines eigenen** (R984): Ein Beleg liegt dort, solange
                keine Zeile in der Ablage ihn hält, und ein Treffer darauf
                trägt deshalb `ordnerId: null`. Ohne diese Übersetzung zählte
                der Baum ihn nirgends und der Filter zeigte ihn nirgends — ein
                Treffer, den die Suche gefunden hat und den niemand sieht.
              */
              ortVon={ortVon}
              nurPeriode={filterperiode}
              aufTreffer={(gefunden) => {
                const karte = new Map<number | null, number>()
                // Der Baum zählt nur Treffer des gewählten Monats (R986).
                for (const x of gefunden.filter((g) => filterperiode === null || g.periode === filterperiode)) {
                  const wo = ortVon(x)
                  karte.set(wo, (karte.get(wo) ?? 0) + 1)
                }
                setTrefferJeOrdner(karte)
              }}
              aufBeleg={(x) => void springeZu(x.ordnerId, x.belegId)}
            />
          )}

          {sicht === 'belege' && belegabruf.ausgefallen && <Abrufausfall abruf={belegabruf} />}
          {sicht === 'belege' && belege === null && !belegabruf.ausgefallen && (
            <p className="hinweis">{t('vorgang.laedt')}</p>
          )}
          {sicht === 'belege' && belege !== null && belege.length === 0 && (
            <p className="hinweis">{t('ordner.leer')}</p>
          )}

          {sicht === 'belege' && belege !== null && belege.length > 0 && (
            <div className="tabellenrahmen belegliste">
              <table ref={spaltenrahmen} className="ziehbaretabelle">
                {/*
                  Die Maße stehen an den Spalten und nicht im Inhalt: Die
                  Namensspalte bekommt, was übrig bleibt — sie trägt die
                  Hauptsache.

                  **Und hier steht keine zweite Spaltenleiste** (Auftrag vom
                  2026-09-29): Zwei `colgroup` nacheinander beschreiben nicht
                  dieselben Spalten zweimal, sondern die ersten und die
                  danach. Diese Liste hat ihre eigene Leiste, seit es sie
                  gibt; eine gezogene Breite setzt sich deshalb an deren
                  Spalten und sticht die Klasse aus.
                */}
                <colgroup>
                  <col className="spalte-kennung" style={gezogen(0)} />
                  <col style={gezogen(1)} />
                  <col className="spalte-periode" style={gezogen(2)} />
                  <col className="spalte-stand" style={gezogen(3)} />
                  <col className="spalte-anwender" style={gezogen(4)} />
                  {vonHandOrdenbar && <col className="spalte-griff" style={gezogen(5)} />}
                </colgroup>
                <thead>
                  <tr>
                    {BELEGSPALTEN.map((s) => (
                      <th
                        key={s.id}
                        className={s.zahl ? 'zahl' : undefined}
                        aria-sort={ariaSortierung(wahl, s.id)}
                      >
                        <Spaltenkopf
                          kopf={t(s.kopf)}
                          wahl={wahl}
                          spalte={s.id}
                          aufKlick={() => setWahl((v) => naechste(v, s.id))}
                        />
                      </th>
                    ))}
                    {/* Die Spalte der Griffe — nur, wo von Hand geordnet wird. */}
                    {vonHandOrdenbar && <th className="griffspalte" />}
                  </tr>
                </thead>
                <tbody ref={belegkoerper} onPointerDown={() => neu !== null && setNeu(null)}>
                  {gezeigteBelege.map((b, stelle) => (
                    <Fragment key={b.id}>
                    <tr
                      tabIndex={0}
                      className={`${offen?.id === b.id ? 'trefferaktiv' : ''}${
                        ablage?.beleg.id === b.id && ablage.schneiden ? ' ausgeschnitten' : ''
                      }${neu !== null && b.nummer === neu ? ' neu' : ''}`}
                      // Ziehen verschiebt — auf einen Ordner links (R983); ohne Recht kein Ziehen (R1180).
                      draggable={darfAblegen}
                      onDragStart={(e) => belegeZiehen(e, einheit, [b])}
                      onClick={() => setOffen(offen?.id === b.id ? null : b)}
                      onContextMenu={(e) => {
                        e.preventDefault()
                        setOffen(b)
                        setMenue({ x: e.clientX, y: e.clientY, beleg: b })
                      }}
                    >
                      {/*
                        **Nummer über Belegart, nicht daneben.** Nebeneinander
                        verlangten die beiden zusammen 330 Pixel und drückten
                        die Namensspalte auf ein Drittel — bei einer Liste, in
                        der der Name die Hauptsache ist.
                      */}
                      <td className="kennung">
                        <span className="belegnummer">{b.nummer}</span>
                        {/*
                          **Der Typ steht da; die Belegart nur, wenn sie mehr
                          sagt** (R391a).

                          Bis zum 2026-09-12 standen beide immer nebeneinander —
                          und an 97 von 100 Belegen sagten sie dasselbe zweimal:
                          *„Manuelle Buchung · Erfassung"*. `erfassung` ist die
                          einzige Belegart ihres Typs, `lieferung` und
                          `konsolidierung` ebenso. Erst unter *Sonstiges* liegen
                          sechs, und dort ist der Unterschied zwischen einem
                          Storno und einer Umgliederung die eigentliche Auskunft.

                          Wer das entscheidet, ist die Ableitung selbst
                          (`SagtDieBelegartMehr`) und nicht diese Stelle: Wenn
                          der Zuschnitt zum Ende der V2 die sechs geparkten Arten
                          verteilt, verschwinden die zweiten Marken von allein.
                        */}
                        <span className={`belegtyp ${b.belegtyp}`}>
                          {t(`belegtyp.${b.belegtyp}` as Sprachschluessel)}
                        </span>
                        {b.belegartSagtMehr && (
                          <span className="belegart schmal">
                            {BELEGART[b.belegart] ? t(BELEGART[b.belegart]!) : b.belegart}
                          </span>
                        )}
                      </td>
                      <td>
                        {/*
                          **Der gespeicherte Text wird mit Namen gelesen, nicht
                          umgeschrieben** (R838): „Saldenliste EH-000005-57 202612"
                          ist die Nachweisspur des Imports und bleibt so im Beleg.

                          **Und mit Monat und Jahr** (7.2 Abschnitt 21, R839 —
                          Entscheidung 3 vom 2026-09-30): Die Zelle zeigt
                          „Saldenliste Holding AG Dezember 2026".
                        */}
                        <div>{b.name ? belegtext(b.name) : '—'}</div>
                        {/* Ohne die Zeilenzahl des Datenimports (R1063). */}
                        {belegerlaeuterung(b.erlaeuterung, b.name) && (
                          <div className="erlaeuterung">{belegerlaeuterung(b.erlaeuterung, b.name)}</div>
                        )}
                        {b.kehrtUm && (
                          <div className="erlaeuterung">{t('beleg.umkehrt', { nummer: b.kehrtUm })}</div>
                        )}
                        {b.umgekehrtDurch && (
                          <div className="erlaeuterung">
                            {t('beleg.umgekehrt', { nummer: b.umgekehrtDurch })}
                          </div>
                        )}
                        {b.storniertDurch && (
                          <div className="erlaeuterung">
                            {t('journal.storniert.durch', { nummer: b.storniertDurch })}
                          </div>
                        )}
                      </td>
                      <td>{periodenname(b.periode)}</td>

                      {/*
                        **Der Stand: das jeweils letzte Datum** (R393).

                        Und er sagt dazu, **welches** es ist. Ein blosses Datum
                        ohne das Wort davor beantwortet die Frage nur halb: Ein
                        Beleg, der am Dienstag erfasst und nie angefasst wurde,
                        und einer, der am Dienstag berichtigt wurde, stehen
                        sonst gleich da — und das ist genau der Unterschied,
                        den ein Prüfer sucht.
                      */}
                      <td className="stand">
                        {/* Mit Uhrzeit: Am Abschlusstag wird mehrfach aktualisiert (R979). */}
                        <div>{zeitpunkt(b.geaendertAm ?? b.erfasstAm)}</div>
                        <div className="erlaeuterung">
                          {t(b.geaendertAm ? 'journal.stand.geaendert' : 'journal.stand.erfasst')}
                        </div>
                      </td>
                      {/* Der Anwender beim Namen, die Maschine bei ihrer Herkunft (R838) —
                          und bei „berichtigt" der, der berichtigt hat (7.2 Abschnitt 42, R1272). */}
                      <td>{anwendername(b.geaendertVon ?? b.erfasstVon)}</td>

                      {/*
                        **Zwei Griffe statt Ziehen mit der Maus** (R143, R392).
                        Ziehen wäre schneller für den, der es kann — und für
                        jeden anderen gäbe es die Funktion nicht. Die Griffe
                        sind mit Maus, Tastatur und Fingerspitze zu bedienen,
                        und wer ziehen will, kann es zusätzlich bekommen.
                      */}
                      {vonHandOrdenbar && (
                        <td className="griffspalte">
                          <button
                            type="button"
                            className="griffknopf"
                            title={t('ordner.hoeher')}
                            aria-label={t('ordner.hoeher')}
                            disabled={stelle === 0}
                            onClick={(e) => {
                              e.stopPropagation()
                              void verschiebe(stelle, -1)
                            }}
                          >
                            ▲
                          </button>
                          <button
                            type="button"
                            className="griffknopf"
                            title={t('ordner.tiefer')}
                            aria-label={t('ordner.tiefer')}
                            disabled={stelle === gezeigteBelege.length - 1}
                            onClick={(e) => {
                              e.stopPropagation()
                              void verschiebe(stelle, 1)
                            }}
                          >
                            ▼
                          </button>
                        </td>
                      )}
                    </tr>

                    {/*
                      **Die Zeilen stehen unter IHRER Zeile**, nicht unter der
                      ganzen Liste. Sie sind der Inhalt dieses einen Belegs;
                      sie hinter alle anderen zu setzen, wäre dieselbe
                      Unklarheit wie ein Ordner neben einer Tabelle.
                    */}
                    {offen?.id === b.id && (
                      <tr className="belegaufriss">
                        <td colSpan={vonHandOrdenbar ? 7 : 6}>
                          {zeilenabruf.ausgefallen ? (
                            <Abrufausfall abruf={zeilenabruf} />
                          ) : zeilen === null ? (
                            <p className="hinweis">{t('vorgang.laedt')}</p>
                          ) : (
                            <Belegzeilen zeilen={zeilen} />
                          )}
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/*
            Die Zeilen stehen seit dem Umbau IN der Liste, unter ihrer eigenen
            Belegzeile — siehe oben.
          */}
        </div>
      </div>

      {menue && (
        <Zeilenmenue
          x={menue.x}
          y={menue.y}
          punkte={belegmenue(menue.beleg)}
          aufSchliessen={() => setMenue(null)}
        />
      )}

      {maske && (
        <Belegmaske
          vorbelegung={maske}
          aufSchliessen={() => setMaske(null)}
          aufGebucht={(nummer, berichtigt) => {
            setMaske(null)
            setAblage(null)
            // „Gebucht" wäre nach einer Berichtigung die falsche Auskunft: Es
            // ist derselbe Beleg, und er ist nicht neu. Ohne Nummer (7.2
            // Abschnitt 34, R1131) — der Beleg steht markiert in der Liste (R1136).
            meldeErfolg(t(berichtigt ? 'erfolg.beleg.berichtigt' : 'erfolg.beleg.gebucht'))
            setMeldung(null)
            setNeu(nummer)
            neuLaden()
          }}
        />
      )}

      {einfuegen && (
        <Einfuegewahl
          was={einfuegen}
          aufWahl={(alsVorlage) => void ausAblage(einfuegen, alsVorlage)}
          aufSchliessen={() => setEinfuegen(null)}
        />
      )}

      {/* Die Umkehrung fragt im Programm (R394); die Ordnerfragen stellt der Baum. */}
      {umkehrfrage && (
        <Nachfrage
          titel={t(UMKEHRTEXTE[umkehrfrage.zeitpunkt ?? 'folgeperiode'].titel)}
          frage={t(UMKEHRTEXTE[umkehrfrage.zeitpunkt ?? 'folgeperiode'].frage, { nummer: umkehrfrage.beleg.nummer })}
          hinweis={t(UMKEHRTEXTE[umkehrfrage.zeitpunkt ?? 'folgeperiode'].hinweis)}
          bestaetigen={t(UMKEHRTEXTE[umkehrfrage.zeitpunkt ?? 'folgeperiode'].titel)}
          aufSchliessen={() => setUmkehrfrage(null)}
          aufJa={() => {
            const { beleg: b, zeitpunkt } = umkehrfrage
            setUmkehrfrage(null)
            void kehreUm(b, zeitpunkt)
          }}
        />
      )}

      {loeschen && (
        <Loeschfrage
          vorschau={loeschen}
          aufSchliessen={() => setLoeschen(null)}
          aufLoeschen={async () => {
            try {
              await api.belegLoeschen(loeschen.belegId)
              setMeldung(t('protokoll.ereignis.geloescht'))
              setOffen(null)
              neuLaden()
            } catch (e) {
              setMeldung(fachlicherSatz(e))
            } finally {
              setLoeschen(null)
            }
          }}
        />
      )}

      {protokoll && (
        <Protokollfenster
          nummer={protokoll.nummer}
          eintraege={protokoll.eintraege}
          aufSchliessen={() => setProtokoll(null)}
        />
      )}
    </>
  )

  function belegmenue(b: Ordnerbeleg): Menuepunkt[] {
    const handerfassung = b.belegart === 'erfassung'

    return [
      /*
        **Bearbeiten steht oben** (R388): Es ist der häufigste Grund, warum
        jemand einen Beleg überhaupt anfasst — der Zahlendreher von gestern.
      */
      {
        schluessel: 'beleg.menue.bearbeiten',
        wenn: handerfassung && !b.storniertDurch && darf('belegAendern'),
        tun: () => void bearbeite(b),
      },
      { schluessel: 'beleg.menue.protokoll', tun: () => void zeigeProtokoll(b) },
      { art: 'trenner' },
      {
        schluessel: 'beleg.menue.umkehren',
        wenn: handerfassung && !b.umgekehrtDurch && !b.storniertDurch && darf('umkehren'),
        // Die Frage stellt das Programm selbst (R394) — `window.confirm` gab
        // hier stillschweigend `false` zurück, und der Menüpunkt tat nichts.
        tun: () => setUmkehrfrage({ beleg: b }),
      },
      // **Oder zum Geschäftsjahresende, oder zu Beginn des neuen** (2.16
      // Abschnitt 7.3, R1832–R1834): Eine Handbuchung steht, bis sie endet.
      {
        schluessel: 'beleg.menue.umkehren.jahresende',
        wenn: handerfassung && !b.umgekehrtDurch && !b.storniertDurch && darf('umkehren'),
        tun: () => setUmkehrfrage({ beleg: b, zeitpunkt: 'jahresende' }),
      },
      {
        schluessel: 'beleg.menue.umkehren.neues-jahr',
        wenn: handerfassung && !b.umgekehrtDurch && !b.storniertDurch && darf('umkehren'),
        tun: () => setUmkehrfrage({ beleg: b, zeitpunkt: 'neues-jahr' }),
      },
      { art: 'trenner' },
      { schluessel: 'beleg.menue.kopieren', wenn: darfBuchen, tun: () => setAblage({ beleg: b, schneiden: false }) },
      {
        schluessel: 'beleg.menue.ausschneiden',
        wenn: handerfassung && darfAblegen,
        tun: () => setAblage({ beleg: b, schneiden: true }),
      },
      { schluessel: 'beleg.menue.einfuegen', wenn: ablage !== null, tun: () => void fuegeEin(ablage!) },
      { art: 'trenner' },
      {
        schluessel: 'beleg.menue.loeschen',
        wenn: handerfassung && darf('belegLoeschen'),
        tun: () => void frageLoeschen(b),
      },
    ]
  }
}

/**
 * Die Zeilen eines aufgeschlagenen Belegs — Soll und Haben, wie sie gebucht
 * wurden.
 */
function Belegzeilen({ zeilen }: { zeilen: Erfassungszeile[] }) {
  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse, rahmen: spaltenrahmen } =
    useSpaltenmasse<HTMLTableElement>('belegordner:belegzeilen')

  return (
    <div className="belegaufrisstabelle">
      <table ref={spaltenrahmen} className="ziehbaretabelle">
        <Spaltenmassleiste masse={spaltenmasse} />
        <thead>
          <tr>
            <th>{t('beleg.spalte.konto')}</th>
            <th>{t('beleg.spalte.bewegungsart')}</th>
            <th>{t('beleg.spalte.partner')}</th>
            <th className="zahl">{t('beleg.spalte.soll')}</th>
            <th className="zahl">{t('beleg.spalte.haben')}</th>
          </tr>
        </thead>
        <tbody>
          {/* Ohne Vortragszeilen (R1063): Sie heben sich im Beleg auf. */}
          {ohneVortragszeilen(zeilen).map((z, i) => (
            <tr key={i}>
              <td>{kontobezeichnung(z.konto)}</td>
              <td>{z.bewegungsart ? bewegungsartname(z.bewegungsart) : ohneWert('weitere')}</td>
              <td>
                {z.icPartner ? gesellschaftsname(z.icPartner) : ohneWert('weitere')}
              </td>
              <td className="zahl">{z.betrag > 0 && <Betrag wert={z.betrag} />}</td>
              <td className="zahl">{z.betrag < 0 && <Betrag wert={-z.betrag} />}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Einfügen: Duplikat oder Vorlage (R376).
 *
 * **Der Unterschied ist nicht kosmetisch.** Ein Duplikat ist ein Beleg und
 * wird gebucht; eine Vorlage ist **kein** Beleg und existiert nur, bis der
 * Anwender sie ausfüllt oder wegwirft. Eine gespeicherte Vorlage wäre ein
 * Beleg ohne Werte, der die Nullsumme verletzt.
 */
function Einfuegewahl({
  was,
  aufWahl,
  aufSchliessen,
}: {
  was: Ablage
  aufWahl: (alsVorlage: boolean) => void
  aufSchliessen: () => void
}) {
  return (
    <div className="nachweisschatten" onClick={aufSchliessen}>
      <div className="fenster" role="dialog" onClick={(e) => e.stopPropagation()}>
        <header>
          {/* Der Name im Satz wie in der Zelle: mit Namen und Monat gelesen (R838, R839). */}
          <h2>
            {t('beleg.einfuegen.frage', {
              name: was.beleg.name != null ? belegtext(was.beleg.name) : was.beleg.nummer,
            })}
          </h2>
        </header>

        <ul className="katalog">
          <li>
            <button type="button" className="katalogeintrag" onClick={() => aufWahl(false)}>
              <span className="name">{t('beleg.einfuegen.duplikat')}</span>
              <span className="zweck">{t('beleg.einfuegen.duplikat.was')}</span>
            </button>
          </li>
          <li>
            <button type="button" className="katalogeintrag" onClick={() => aufWahl(true)}>
              <span className="name">{t('beleg.einfuegen.vorlage')}</span>
              <span className="zweck">{t('beleg.einfuegen.vorlage.was')}</span>
            </button>
          </li>
        </ul>

        <button type="button" onClick={aufSchliessen}>
          {t('beleg.abbrechen')}
        </button>
      </div>
    </div>
  )
}

/**
 * Die Warnabfrage vor dem Löschen (R380).
 *
 * **Kein „Wirklich löschen?".** Nummer, Name, Summe, Zeilenzahl — und die
 * Zahl der Läufe, die dadurch veralten, sowie die Umkehrbuchung, die mitgeht.
 */
function Loeschfrage({
  vorschau,
  aufLoeschen,
  aufSchliessen,
}: {
  vorschau: Loeschvorschau
  aufLoeschen: () => void
  aufSchliessen: () => void
}) {
  const gesperrt = vorschau.hindernis !== null

  return (
    <div className="nachweisschatten" onClick={aufSchliessen}>
      <div className="fenster" role="dialog" onClick={(e) => e.stopPropagation()}>
        <header>
          <h2>{t('loeschen.titel')}</h2>
        </header>

        {gesperrt ? (
          <>
            <p className="probe abweichung">
              {t(vorschau.hindernis!.schluessel as Sprachschluessel, werte(vorschau.hindernis!))}
            </p>
            <p className="erlaeuterung">{t('loeschen.hindernis.weg')}</p>
          </>
        ) : (
          <>
            <p className="probe abweichung">
              {t('loeschen.frage', {
                nummer: vorschau.nummer,
                // Der Name im Satz wie in der Zelle: mit Namen und Monat gelesen (R838, R839).
                name: vorschau.name != null ? belegtext(vorschau.name) : '—',
                summe: formatiert(vorschau.summe),
                zeilen: anzahlwort(vorschau.zeilen),
              })}
            </p>

            {vorschau.laeufe > 0 && (
              <p className="hinweis">{t('loeschen.laeufe', { anzahl: anzahlwort(vorschau.laeufe) })}</p>
            )}
            {vorschau.freigegeben > 0 && (
              <p className="hinweis">{t('loeschen.freigegeben', { anzahl: anzahlwort(vorschau.freigegeben) })}</p>
            )}
            {vorschau.umkehrung && (
              <p className="hinweis">{t('loeschen.umkehrung', { nummer: vorschau.umkehrung })}</p>
            )}

            {/*
              **Das Änderungsprotokoll überlebt** (R378) — und genau das ist
              die Bedingung, unter der hier überhaupt gelöscht werden darf.
              Es gehört deshalb in die Abfrage und nicht in eine Fußnote.
            */}
            <p className="erlaeuterung">{t('loeschen.protokoll')}</p>
          </>
        )}

        <div className="aktionsreihe">
          {!gesperrt && (
            <button type="button" className="gefahr" onClick={aufLoeschen}>
              {t('loeschen.tun')}
            </button>
          )}
          <button type="button" onClick={aufSchliessen}>
            {t('beleg.abbrechen')}
          </button>
        </div>
      </div>
    </div>
  )
}

/** Das Änderungsprotokoll eines Belegs — mit Zeitstempel und Wertnachweis (R378). */
function Protokollfenster({
  nummer,
  eintraege,
  aufSchliessen,
}: {
  nummer: string
  eintraege: Protokolleintrag[]
  aufSchliessen: () => void
}) {
  /*
    **Das übliche Fenster** (7.2 Abschnitt 41, R1260): verschieben am Kopf,
    groß und klein im Kopf, die Ecke zieht, Esc und Kreuz schließen — bis zum
    2026-10-06 schloss hier weder Esc noch ein Kreuz, nur der Knopf im Kopf
    und der Klick daneben.
    Mittelbreit, weil ein Beleg eine Handvoll Einträge trägt und die Tabelle
    darin vier Spalten; wer mehr Fläche will, schaltet groß.
  */
  return (
    <Dialogfenster titel={t('protokoll.titel', { nummer })} weite="mittel" aufSchliessen={aufSchliessen}>
      <div className="nachweis">
        {eintraege.length === 0 && <p className="hinweis">{t('protokoll.leer')}</p>}

        {/*
          **Alles offen.** Ein Beleg hat eine Handvoll Protokolleinträge, keine
          dreitausend — und wer das Protokoll öffnet, will es lesen und nicht
          erst aufklappen.
        */}
        {eintraege.map((e) => (
          <details key={e.id} className="vorgang" open>
            <summary>
              <span className="nummer">{zeitpunkt(e.zeitpunkt)}</span>
              <span className="name">
                {EREIGNIS[e.ereignis] ? t(EREIGNIS[e.ereignis]!) : e.ereignis}
              </span>
              <span className="nummer">{anwendername(e.anwender)}</span>
              {e.summe !== null && <span className="nummer"><Betrag wert={e.summe} /></span>}
            </summary>

            <div style={{ padding: '10px 12px' }}>
              {/*
                **„X → X" ist keine Auskunft.** Beim Berichtigen trägt der
                Eintrag Namen vorher und nachher, auch wenn nur ein Betrag
                berichtigt wurde — denselben Namen zweimal mit einem Pfeil
                dazwischen zu zeigen, behauptet eine Änderung, die es nicht gab.
              */}
              {(e.vorher || e.nachher) && e.vorher !== e.nachher && (
                <p className="erlaeuterung" style={{ marginTop: 0 }}>
                  {belegtext([e.vorher, e.nachher].filter(Boolean).join(' → '))}
                </p>
              )}

              {/*
                **Der vollständige Stand davor — auf Klick** (R390).

                Beim Berichtigen trägt der Eintrag die ganze Vorgängerfassung:
                Kopf und jede Zeile. Aufgeklappt stünde sie bei einem
                vierzigzeiligen Beleg zwischen den anderen Einträgen und machte
                das Protokoll unlesbar; zugeklappt ist sie ein Klick weit weg
                und für den Prüfer da, der sie sehen will.
              */}
              {e.kopf !== null && (
                <details className="vorstand">
                  <summary>{t('protokoll.vorher')}</summary>

                  <dl className="vorstandkopf">
                    <dt>{t('protokoll.vorher.name')}</dt>
                    <dd>{e.kopf.name ? belegtext(e.kopf.name) : '—'}</dd>
                    <dt>{t('protokoll.vorher.erlaeuterung')}</dt>
                    <dd>{e.kopf.erlaeuterung ? belegtext(e.kopf.erlaeuterung) : '—'}</dd>
                    <dt>{t('protokoll.vorher.begruendung')}</dt>
                    <dd>{e.kopf.begruendung || '—'}</dd>
                  </dl>

                  <Wertnachweistabelle zeilen={e.zeilen} />
                </details>
              )}

              {e.kopf === null && e.zeilen.length > 0 && (
                <>
                  <p className="erlaeuterung">{t('protokoll.wertnachweis')}</p>
                  <Wertnachweistabelle zeilen={e.zeilen} />
                </>
              )}
            </div>
          </details>
        ))}
      </div>
    </Dialogfenster>
  )
}

/** Der Wertnachweis als Tabelle — ein Weg, ob Löschung oder Vorgängerfassung. */
function Wertnachweistabelle({ zeilen }: { zeilen: Protokollzeile[] }) {
  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse, rahmen: spaltenrahmen } =
    useSpaltenmasse<HTMLTableElement>('belegordner:wertnachweistabelle')

  if (zeilen.length === 0) {
    return <p className="hinweis">{t('protokoll.vorher.leer')}</p>
  }

  return (
    <div className="tabellenrahmen">
      <table ref={spaltenrahmen} className="ziehbaretabelle">
        <Spaltenmassleiste masse={spaltenmasse} />
        <thead>
          <tr>
            <th>{t('beleg.spalte.konto')}</th>
            <th>{t('beleg.spalte.bewegungsart')}</th>
            <th>{t('beleg.spalte.partner')}</th>
            <th className="zahl">{t('vorgang.spalte.betrag')}</th>
          </tr>
        </thead>
        <tbody>
          {ohneVortragszeilen(zeilen).map((z, i) => (
            <tr key={i}>
              <td>{kontobezeichnung(z.konto)}</td>
              <td>{bewegungsartname(z.bewegungsart)}</td>
              <td>{z.partner ? gesellschaftsname(z.partner) : ohneWert('weitere')}</td>
              <td className={`zahl ${z.betrag < 0 ? 'minus' : ''}`}>
                <Betrag wert={z.betrag} waehrung={z.waehrung} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function werte(baustein: { werte: { feld: string; wert: string }[] }): Record<string, string> {
  const karte: Record<string, string> = {}
  for (const w of baustein.werte ?? []) {
    karte[w.feld] = w.wert
  }
  return karte
}
